import { useEffect, useRef } from "react";

const DEBOUNCE_DELAY = 150;

function calculateEmbedHeight() {
  const { body, documentElement } = document;

  return Math.max(
    body?.scrollHeight || 0,
    documentElement?.scrollHeight || 0,
    body?.offsetHeight || 0,
    documentElement?.offsetHeight || 0
  );
}

export default function useEmbedHeight(enabled) {
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const scheduleHeightPost = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const height = calculateEmbedHeight();
        window.parent?.postMessage({ type: "NOUVAC_EMBED_HEIGHT", height }, "*");
      }, DEBOUNCE_DELAY);
    };

    const sendReadyAndHeight = () => {
      window.parent?.postMessage({ type: "NOUVAC_EMBED_READY" }, "*");
      scheduleHeightPost();
    };

    const resizeObserver = new ResizeObserver(scheduleHeightPost);
    const mutationObserver = new MutationObserver(scheduleHeightPost);

    resizeObserver.observe(document.body);
    mutationObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    const rafId = requestAnimationFrame(sendReadyAndHeight);

    window.addEventListener("resize", scheduleHeightPost);

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", scheduleHeightPost);
    };
  }, [enabled]);
}
